import {
  type AttorneyGeneralDocumentRow,
  type AttorneyGeneralWizardDraft,
  newAttorneyGeneralAccused,
} from '../types/attorneyGeneralCaseRequest';
import {
  ATTORNEY_GENERAL_CATEGORY_ID,
  FILE_CATEGORY_ATTORNEY_GENERAL,
} from '../pages/wizard/attorneyGeneral/attorneyGeneralConstants';
import * as attorneyGeneralService from '../services/attorneyGeneralCaseRequests';
import { isDocumentTypeUuid } from '../utils/documentTypeId';

export const DEFAULT_ATTORNEY_GENERAL_DRAFT: AttorneyGeneralWizardDraft = {
  fileCategory: FILE_CATEGORY_ATTORNEY_GENERAL,
  fileCategoryId: '',
  subFileCategoryId: '',
  subFileCategoryName: '',
  applicableAreas: [],
  applicableAreaIds: [],

  description: '',
  provinceId: '',
  provinceName: '',
  districtId: '',
  districtName: '',
  caseSource: '',
  highCourtId: '',
  highCourtName: '',
  magistrateCourtId: '',
  magistrateCourtName: '',
  policeStationId: '',
  policeStationName: '',
  countryInvolvedId: '',
  countryInvolvedName: '',
  directionOfRequest: '',
  yourReferenceNumber: '',
  otherExternalReferences: '',
  hasPreviousAdvice: 'no',
  previousAdviceUploads: [],
  hasConnectedAdviceCase: 'no',
  connectedAdviceDetails: '',
  hasActionInstituted: 'no',
  courtTypeId: '',
  courtTypeName: '',
  courtTypeOther: '',
  courtId: '',
  courtName: '',
  isAppealMatter: 'no',
  originalCourt: '',
  judgementOrderUploads: [],
  caseNumber: '',
  hasConnectedMatters: 'no',
  connectedCaseNumber: '',
  connectedAgdFileRef: '',

  plaintiffName: '',
  defendantName: '',
  partyBodyType: 'government',
  otherInvolvedInstitutions: '',
  legalOfficerName: '',
  legalOfficerContact: '',

  subjectOfDispute: '',
  incidentDateFrom: '',
  incidentDateTo: '',
  nextStepOfCaseId: '',
  nextStepOfCaseName: '',
  nextStepOfCaseOther: '',
  nextCaseDate: '',

  victimName: '',
  victimDob: '',
  victimGenderId: '',
  victimGenderName: '',

  offenceDateFrom: '',
  offenceDateTo: '',
  placeOfOffence: '',
  accused: [newAttorneyGeneralAccused()],

  accusedName: '',
  nationalityId: '',
  nationalityName: '',
  countryOfOriginId: '',
  countryOfOriginName: '',
  accusedDob: '',
  accusedGenderId: '',
  accusedGenderName: '',
  passportOrIdNumber: '',
  custodyStatusId: '',
  custodyStatusName: '',
  custodialAuthority: '',
  lastKnownLocation: '',

  arrestWarrantAvailable: 'no',
  warrantReference: '',
  applicableTreatyArrangement: '',

  petitionerName: '',
  petitionerNic: '',
  petitionerAddress: '',
  petitionerContact: '',
  petitionerEmail: '',
  representedByAttorney: 'no',
  attorneyName: '',
  attorneyContact: '',
  respondentNames: '',
  respondentType: '',
  respondentTypeName: '',
  respondentAddress: '',
  numberOfRespondents: '',

  rtiRequestorName: '',
  rtiNicOrPassport: '',
  rtiAddress: '',
  rtiContactNumber: '',
  rtiEmailAddress: '',
  rtiIsSriLankanCitizen: '',
  rtiSpecificPublicAuthorityName: '',
  rtiRequestedInformationDetails: '',
  rtiInformationRequestedTimePeriod: '',
  rtiPreferredMethodOfAccessIds: [],
  rtiPreferredMethodOfAccessNames: [],
  rtiPreferredLanguageId: '',
  rtiPreferredLanguageName: '',
  rtiInvolvesLifeOrPersonalLiberty: '',
  rtiJustification: '',
  rtiAdditionalNotes: '',

  urgencyLevelId: '',
  urgencyLevelName: '',
  deadline: '',
  urgencyJustification: '',

  deliveryMethodIds: [],
  deliveryMethods: [],
  deliveryOther: '',

  documents: [],
  confirmCorrect: false,

  draftRequestId: '',
  referenceNo: '',
};

export type AttorneyGeneralWizardContextValue = {
  draft: AttorneyGeneralWizardDraft;
  setDraft: React.Dispatch<React.SetStateAction<AttorneyGeneralWizardDraft>>;
  reset: () => void;
};

function normalizeDocLabel(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findDocumentForChecklistItem(
  prev: AttorneyGeneralDocumentRow[],
  item: { id: string; documentTypeId: string; name: string; documentTypeCode?: string },
): AttorneyGeneralDocumentRow | undefined {
  return prev.find((d) => {
    if (d.checklistItemId === item.id || d.documentTypeId === item.documentTypeId) return true;
    if (item.documentTypeCode) {
      if (d.documentTypeId === item.documentTypeCode || d.checklistItemId === item.documentTypeCode) return true;
    }
    if (!isDocumentTypeUuid(d.documentTypeId) && normalizeDocLabel(d.documentName) === normalizeDocLabel(item.name)) {
      return true;
    }
    return false;
  });
}

export function syncAttorneyGeneralDocumentsFromChecklist(
  prev: AttorneyGeneralDocumentRow[],
  checklist: { id: string; documentTypeId: string; name: string; mandatory: boolean; documentTypeCode?: string }[],
): AttorneyGeneralDocumentRow[] {
  return checklist.map((item) => {
    const existing = findDocumentForChecklistItem(prev, item);
    if (existing) {
      return {
        ...existing,
        checklistItemId: item.id,
        documentTypeId: item.documentTypeId,
        documentName: item.name,
        mandatory: item.mandatory,
      };
    }
    return {
      id: crypto.randomUUID(),
      checklistItemId: item.id,
      documentTypeId: item.documentTypeId,
      documentName: item.name,
      mandatory: item.mandatory,
      fileName: '',
      originalFileName: '',
      uploadTime: '',
      skipReason: '',
    };
  });
}

export { ATTORNEY_GENERAL_CATEGORY_ID };
